<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import BusinessSummaryBar from 'dashboard/components/BusinessSummaryBar.vue';
import AipayOKSupportAPI from 'dashboard/api/aipayokSupport';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import LegacyQueueStatusBadge from './LegacyQueueStatusBadge.vue';
import TransferBanner from './TransferBanner.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();
const { updateUISettings } = useUISettings();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

// Business data state
const businessData = ref(null);
const isLoadingBusinessData = ref(false);
const hasBusinessDataError = ref(false);
const isCancellingFollowUp = ref(false);
const isTakingOverFollowUp = ref(false);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isAipayOKConversation = computed(() => {
  const conversationAttributes = props.chat.custom_attributes || {};
  return (
    conversationAttributes.source_site === 'AipayOK' ||
    (isHMACVerified.value &&
      /^\d+$/.test(String(currentContact.value?.identifier || '')))
  );
});

const shouldShowBusinessBar = computed(
  () =>
    isHMACVerified.value && isAipayOKConversation.value && businessData.value
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const followUpMetadata = computed(
  () => currentChat.value.additional_attributes?.aipayok_follow_up || {}
);
const isFollowUpActive = computed(() =>
  ['scheduled', 'due'].includes(followUpMetadata.value.state)
);
const isFollowUpDue = computed(() => followUpMetadata.value.state === 'due');
const currentUser = computed(() => store.getters.getCurrentUser);
const canTakeOverFollowUp = computed(
  () =>
    isFollowUpActive.value &&
    String(followUpMetadata.value.owner_id || '') !==
      String(currentUser.value?.id || '')
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};

const fetchBusinessData = async () => {
  if (!isHMACVerified.value || !isAipayOKConversation.value || !props.chat.id) {
    businessData.value = null;
    return;
  }

  isLoadingBusinessData.value = true;
  hasBusinessDataError.value = false;

  try {
    const { data } = await AipayOKSupportAPI.show(props.chat.id);

    businessData.value = data;
  } catch (error) {
    hasBusinessDataError.value = true;
    businessData.value = null;
  } finally {
    isLoadingBusinessData.value = false;
  }
};

const handleToggleBusinessDetail = () => {
  // Open the contact panel to show full business context
  updateUISettings({
    is_contact_sidebar_open: true,
  });
};

const cancelFollowUp = async () => {
  if (isCancellingFollowUp.value) return;

  isCancellingFollowUp.value = true;
  try {
    await store.dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: wootConstants.STATUS_TYPE.OPEN,
      cancelFollowUp: true,
    });
    await store.dispatch('getConversation', currentChat.value.id);
    useAlert(t('CONVERSATION.HEADER.CANCEL_FOLLOW_UP_SUCCESS'));
  } catch {
    useAlert(t('CONVERSATION.HEADER.CANCEL_FOLLOW_UP_FAILED'));
  } finally {
    isCancellingFollowUp.value = false;
  }
};

const takeOverFollowUp = async () => {
  if (isTakingOverFollowUp.value) return;

  isTakingOverFollowUp.value = true;
  try {
    await store.dispatch('takeOverFollowUp', {
      conversationId: currentChat.value.id,
      expectedOwnerId: followUpMetadata.value.owner_id,
    });
    await store.dispatch('getConversation', currentChat.value.id);
    useAlert(t('CONVERSATION.HEADER.TAKE_OVER_FOLLOW_UP_SUCCESS'));
  } catch {
    useAlert(t('CONVERSATION.HEADER.TAKE_OVER_FOLLOW_UP_FAILED'));
  } finally {
    isTakingOverFollowUp.value = false;
  }
};

// Watch for conversation changes and fetch business data
watch(
  () => [props.chat.id, isHMACVerified.value, currentContact.value?.identifier],
  () => {
    fetchBusinessData();
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col w-full">
    <div
      ref="conversationHeader"
      class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
    >
      <div
        class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
      >
        <BackButton
          v-if="showBackButton"
          :back-url="backButtonUrl"
          class="ltr:mr-2 rtl:ml-2"
        />
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="32"
          :status="currentContact.availability_status"
          hide-offline-status
        />
        <div
          class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
        >
          <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
            <span
              class="text-sm font-medium truncate leading-tight text-n-slate-12"
            >
              {{ currentContact.name }}
            </span>
            <fluent-icon
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              size="14"
              class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
              icon="warning"
            />
            <LegacyQueueStatusBadge :conversation="chat" />
          </div>

          <div
            class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
          >
            <button
              type="button"
              class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
              @click="copyConversationId"
            >
              {{ `#${chat.id}` }}
            </button>
            <span v-if="hasMultipleInboxes">•</span>
            <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
            <span v-if="isSnoozed">•</span>
            <span v-if="isSnoozed" class="font-medium text-n-amber-10">
              {{ snoozedDisplayText }}
            </span>
            <span
              v-if="isFollowUpDue"
              class="rounded bg-n-ruby-3 px-1.5 py-0.5 font-medium text-n-ruby-11"
            >
              {{ t('CONVERSATION.HEADER.FOLLOW_UP_DUE') }}
            </span>
            <span
              v-if="isFollowUpActive && followUpMetadata.reason"
              class="truncate font-medium text-n-amber-10"
              :title="followUpMetadata.reason"
            >
              {{
                t('CONVERSATION.HEADER.FOLLOW_UP_REASON', {
                  reason: followUpMetadata.reason,
                })
              }}
            </span>
            <span
              v-if="isFollowUpActive && followUpMetadata.owner_name"
              class="shrink-0 font-medium text-n-slate-11"
            >
              {{
                t('CONVERSATION.HEADER.FOLLOW_UP_OWNER', {
                  owner: followUpMetadata.owner_name,
                })
              }}
            </span>
            <button
              v-if="canTakeOverFollowUp"
              data-testid="take-over-follow-up"
              type="button"
              class="shrink-0 rounded px-1.5 py-0.5 font-medium text-n-blue-11 hover:bg-n-blue-3 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="isTakingOverFollowUp"
              @click="takeOverFollowUp"
            >
              {{ t('CONVERSATION.HEADER.TAKE_OVER_FOLLOW_UP') }}
            </button>
            <button
              v-if="isFollowUpActive"
              data-testid="cancel-follow-up"
              type="button"
              class="shrink-0 rounded px-1.5 py-0.5 font-medium text-n-ruby-11 hover:bg-n-ruby-3 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="isCancellingFollowUp"
              @click="cancelFollowUp"
            >
              {{ t('CONVERSATION.HEADER.CANCEL_FOLLOW_UP') }}
            </button>
          </div>
        </div>
      </div>
      <div
        class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
      >
        <SLACardLabel
          v-if="hasSlaPolicyId"
          :chat="chat"
          show-extended-info
          :parent-width="width"
          class="hidden md:flex"
        />
        <ConversationCallButton :inbox="inbox" :chat="currentChat" />
        <MoreActions :conversation-id="currentChat.id" />
      </div>
    </div>

    <TransferBanner :conversation="currentChat" />
    <!-- Business Summary Bar -->
    <BusinessSummaryBar
      v-if="shouldShowBusinessBar"
      :business-data="businessData"
      @show-detail="handleToggleBusinessDetail"
    />
  </div>
</template>
